import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { supabase } from "@/lib/supabase"

import { SessionInvalidation } from "./useSessionInvalidation"

export interface CreateSessionInvalidationInput {
  user_id?: string | null
  invalidate_at: string
  is_active?: boolean
  reason?: string | null
}

export interface UpdateSessionInvalidationInput {
  id: string
  user_id?: string | null
  invalidate_at?: string
  is_active?: boolean
  reason?: string | null
}

/**
 * Hook to fetch all session invalidations (admin only)
 */
export function useSessionInvalidations() {
  return useQuery<SessionInvalidation[]>({
    queryKey: ["session-invalidations"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("session_invalidations")
        .select("*")
        .order("created_at", { ascending: false })

      if (error) {
        throw new Error(`Failed to fetch session invalidations: ${error.message}`)
      }

      return (data as SessionInvalidation[]) || []
    },
  })
}

/**
 * Hook to create a new session invalidation
 */
export function useCreateSessionInvalidation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: CreateSessionInvalidationInput) => {
      const {
        data: { session },
      } = await supabase.auth.getSession()
      if (!session?.user) {
        throw new Error("Not authenticated")
      }

      const { data, error } = await supabase
        .from("session_invalidations")
        .insert({
          user_id: input.user_id || null,
          invalidate_at: input.invalidate_at,
          is_active: input.is_active ?? true,
          reason: input.reason || null,
          created_by: session.user.id,
        })
        .select()
        .single()

      if (error) {
        throw new Error(`Failed to create session invalidation: ${error.message}`)
      }

      return data as SessionInvalidation
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["session-invalidations"] })
    },
  })
}

/**
 * Hook to update a session invalidation
 */
export function useUpdateSessionInvalidation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: UpdateSessionInvalidationInput) => {
      const { id, ...updates } = input
      const { data, error } = await supabase
        .from("session_invalidations")
        .update(updates)
        .eq("id", id)
        .select()
        .single()

      if (error) {
        throw new Error(`Failed to update session invalidation: ${error.message}`)
      }

      return data as SessionInvalidation
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["session-invalidations"] })
    },
  })
}

/**
 * Hook to delete a session invalidation
 */
export function useDeleteSessionInvalidation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("session_invalidations").delete().eq("id", id)

      if (error) {
        throw new Error(`Failed to delete session invalidation: ${error.message}`)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["session-invalidations"] })
    },
  })
}
